import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle, AlertTriangle, Clock, Send } from 'lucide-react';
import { toast, Toaster } from 'sonner';

var API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function DataCollectionPage() {
  var params = useParams();
  var token = params.token;
  var _s1 = useState(null);
  var formData = _s1[0], setFormData = _s1[1];
  var _s2 = useState(true);
  var loading = _s2[0], setLoading = _s2[1];
  var _s3 = useState({});
  var values = _s3[0], setValues = _s3[1];
  var _s4 = useState(false);
  var submitting = _s4[0], setSubmitting = _s4[1];
  var _s5 = useState(false);
  var submitted = _s5[0], setSubmitted = _s5[1];

  useEffect(function() {
    fetch(API_BASE + '/api/collect/' + token)
      .then(function(r) { return r.json(); })
      .then(function(data) { setFormData(data); })
      .catch(function() { setFormData({ status: 'error' }); })
      .finally(function() { setLoading(false); });
  }, [token]);

  function handleChange(key, val) {
    var next = Object.assign({}, values);
    next[key] = val;
    setValues(next);
  }

  function handleSubmit() {
    var filledCount = 0;
    var keys = Object.keys(values);
    for (var i = 0; i < keys.length; i++) {
      if (values[keys[i]] && values[keys[i]].trim()) filledCount++;
    }
    if (filledCount === 0) { toast.error('Please fill in at least one field'); return; }
    setSubmitting(true);
    fetch(API_BASE + '/api/collect/' + token, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(values),
    })
      .then(function(r) {
        if (!r.ok) throw new Error('Submission failed');
        return r.json();
      })
      .then(function(data) {
        setSubmitted(true);
        toast.success('Information submitted successfully! (' + data.fields_updated + ' fields updated)');
      })
      .catch(function() { toast.error('Failed to submit. Please try again.'); })
      .finally(function() { setSubmitting(false); });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!formData || formData.status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Request Not Found</h2>
            <p className="text-sm text-muted-foreground">This link is invalid or has been removed. Please contact your immigration consultant.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (formData.status === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <Clock className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Link Expired</h2>
            <p className="text-sm text-muted-foreground">This data collection link has expired. Please contact your consultant for a new link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (formData.status === 'already_submitted' || submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
        <Toaster position="top-center" />
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Thank You!</h2>
            <p className="text-sm text-muted-foreground">Your information has been submitted successfully. Your consultant will be notified.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  var missingKeys = formData.missing_keys || [];
  var missingLabels = formData.missing_labels || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8 px-4">
      <Toaster position="top-center" />
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Information Request</h1>
          <p className="text-sm text-muted-foreground mt-1">for <span className="font-medium text-slate-700">{formData.form_name}</span></p>
        </div>

        <Card data-testid="collection-form">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Please fill in the following information</CardTitle>
            <p className="text-xs text-muted-foreground">Fields marked are needed to complete your immigration form. Fill in as many as you can.</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {missingKeys.map(function(key, idx) {
                var label = missingLabels[idx] || key;
                var isSensitive = key === 'ssn' || key === 'a_number' || key === 'passport_number';
                return (
                  <div key={key} className="space-y-1" data-testid={'field-' + key}>
                    <Label className="text-sm font-medium">{label}</Label>
                    <Input
                      type={isSensitive ? 'password' : 'text'}
                      placeholder={'Enter ' + label.toLowerCase()}
                      value={values[key] || ''}
                      onChange={function(e) { handleChange(key, e.target.value); }}
                      className="h-9 text-sm"
                      data-testid={'input-' + key}
                    />
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex flex-col gap-3">
              <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-data-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Submit Information
              </Button>
              <p className="text-[11px] text-center text-muted-foreground">
                Your data is encrypted and securely transmitted. Only your assigned consultant can access it.
              </p>
            </div>
          </CardContent>
        </Card>

        {formData.expires_at ? (
          <p className="text-xs text-center text-muted-foreground mt-4">
            <Clock className="w-3 h-3 inline mr-1" />
            This link expires on {formData.expires_at.substring(0, 10)}
          </p>
        ) : null}
      </div>
    </div>
  );
}
